import { encontrarEnqueteLegada } from "@/stores/app/chatUtils";
import type { ExportarConversaResponse } from "@/services/mensagens/mensagens.service";
import { ehVideoRecado } from "@/utils/videoRecado";

/**
 * Monta a transcrição da conversa em HTML estilo chat (WhatsApp) e abre o
 * diálogo de impressão do navegador, onde o usuário pode "Salvar como PDF".
 *
 * Mídias (fotos, áudios, vídeos, documentos) entram como links clicáveis.
 */

function toStr(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return "";
}

function esc(v: unknown): string {
  return toStr(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\n/g, "<br>");
}

// conteudo vem do Cassandra como map<text,text>: estruturas aninhadas
// (secoes, opcoes, botoes, contatos) chegam como string JSON.
function parseTalvezJson<T>(v: unknown): T | null {
  if (typeof v !== "string") return (v as T) ?? null;
  const t = v.trim();
  if (!t.startsWith("{") && !t.startsWith("[")) return null;
  try {
    return JSON.parse(t) as T;
  } catch {
    return null;
  }
}

function fmtData(d: Date, timeZone: string): string {
  return d.toLocaleString("pt-BR", { timeZone });
}

function fmtDia(d: Date, timeZone: string): string {
  return d.toLocaleDateString("pt-BR", { timeZone, dateStyle: "full" });
}

function dataDaMensagem(m: { dataEnvio?: string; dataCriacao?: string }): Date {
  if (m.dataEnvio) return new Date(m.dataEnvio);
  if (m.dataCriacao) return new Date(m.dataCriacao);
  return new Date(0);
}

/**
 * `href` só com http(s): o PDF é montado num `window.open('')` do mesmo
 * domínio do app, e um `javascript:` vindo da mensagem rodaria no clique.
 */
function hrefSeguro(url: unknown): string {
  const u = toStr(url).trim();
  return /^https?:\/\//i.test(u) ? esc(u) : "#";
}

function linkMidia(rotulo: string, url?: string, legenda?: unknown): string {
  const legendaHtml = legenda
    ? `<div class="legenda">${esc(legenda)}</div>`
    : "";
  if (!url)
    return `<div class="anexo">${rotulo} — mídia não disponível</div>${legendaHtml}`;
  return `<div class="anexo">${rotulo} — <a href="${hrefSeguro(url)}">abrir mídia</a></div>${legendaHtml}`;
}

type ContatoCompartilhado = { nome?: unknown; telefone?: unknown };
type ItemLista = { titulo?: unknown; descricao?: unknown };
type SecaoLista = { titulo?: unknown; items?: ItemLista[] };
type OpcaoEnquete = { opcao?: unknown };
type BotaoInterativo = { texto?: unknown; titulo?: unknown; id?: unknown };

function renderConteudo(m: {
  conteudo?: Record<string, unknown>;
  apagada?: boolean;
}): string {
  const c = m.conteudo || {};
  const tipo = typeof c.tipo === "string" ? c.tipo : "?";

  if (m.apagada) return '<div class="sistema">🚫 Mensagem apagada</div>';

  const url = typeof c.url === "string" ? c.url : undefined;
  const legenda = c.legenda;

  // Enquete gravada antes do fix de variantes: o servidor não a reconheceu e o
  // `tipo` ficou GENERICA, então `case "ENQUETE"` nunca seria alcançado e ela
  // cairia no `default`, que procura `texto`/`corpo`/`legenda`/... — nenhum
  // deles existe numa enquete crua, e a linha sairia VAZIA no PDF. É a mesma
  // recuperação que `MakeMessage.Tipo.Resolve` faz na tela; sem ela, a mesma
  // conversa mostra o card no chat e nada no export.
  if (tipo !== "ENQUETE") {
    const legada = encontrarEnqueteLegada(c);
    if (legada?.name) {
      const opcoes = Array.isArray(legada.options) ? legada.options : [];
      const itens = opcoes
        .map(o => `<li>${esc((o as { optionName?: unknown })?.optionName)}</li>`)
        .join("");
      // Sem contagem: voto nunca foi apurado para estas mensagens.
      return `<div class="texto">📊 Enquete: <b>${esc(legada.name)}</b></div><div class="opcoes"><ul>${itens}</ul></div>`;
    }
  }

  // Vídeo-recado (`ptvMessage`) gravado como GENERICA: mesma recuperação de
  // `MakeMessage.Tipo.Resolve` na tela.
  if (tipo !== "VIDEO" && ehVideoRecado(c)) return linkMidia("🎬 Vídeo", url, legenda);

  switch (tipo) {
    case "TEXTO":
      return `<div class="texto">${esc(c.texto)}</div>`;

    case "IMAGEM":
      return linkMidia("🖼 Foto", url, legenda);
    case "FIGURINHA":
      return linkMidia("🌟 Figurinha", url, legenda);
    case "VIDEO":
      return linkMidia("🎬 Vídeo", url, legenda);
    case "AUDIO":
      return linkMidia("🎵 Áudio", url, legenda);
    case "LOTTIE":
      return linkMidia("✨ Animação", url, legenda);
    case "DOCUMENTO":
      return linkMidia(
        `📄 ${esc(c.nome_documento || "Documento")}`,
        url,
        legenda,
      );

    case "CONTATO":
      return `<div class="anexo">👤 Contato compartilhado: <b>${esc(c.nome)}</b> ${esc(c.telefone)}</div>`;

    case "CONTATOS": {
      const contatos = parseTalvezJson<ContatoCompartilhado[]>(c.contatos);
      const lista = Array.isArray(contatos)
        ? contatos
            .map((ct) => `<li><b>${esc(ct.nome)}</b> ${esc(ct.telefone)}</li>`)
            .join("")
        : "";
      return `<div class="anexo">👤 Contatos compartilhados:<ul>${lista}</ul></div>`;
    }

    case "LOCALIZACAO": {
      const urlMapa =
        url ||
        `https://maps.google.com/?q=${esc(c.latitude)},${esc(c.longitude)}`;
      const nome = [c.nome, c.endereco].filter(Boolean).map(esc).join(" — ");
      return `<div class="anexo">📍 Localização${nome ? `: <b>${nome}</b>` : ""} — <a href="${hrefSeguro(urlMapa)}">ver no mapa</a></div>`;
    }

    case "REACAO":
      return `<div class="sistema">Reagiu com ${esc(c.reacao)} a uma mensagem</div>`;

    case "LISTA": {
      const secoes = parseTalvezJson<SecaoLista[]>(c.secoes);
      const itens = Array.isArray(secoes)
        ? secoes
            .map((sec) => {
              const items = (sec.items ?? [])
                .map(
                  (it) =>
                    `<li>${esc(it.titulo)}${it.descricao ? ` — <i>${esc(it.descricao)}</i>` : ""}</li>`,
                )
                .join("");
              return `${sec.titulo ? `<div><b>${esc(sec.titulo)}</b></div>` : ""}<ul>${items}</ul>`;
            })
            .join("")
        : "";
      return `<div class="texto">${esc(c.titulo)}${c.descricao ? `<br>${esc(c.descricao)}` : ""}</div><div class="opcoes">📋 Opções:${itens}</div>`;
    }

    case "LISTA_RESPOSTA":
      return `<div class="texto">${esc(c.texto || c.idResposta)}</div><div class="sistema">(opção escolhida da lista)</div>`;

    case "ENQUETE": {
      const opcoes = parseTalvezJson<(OpcaoEnquete | string)[]>(c.opcoes);
      // Votos por índice de opção, gravados pelo servidor a cada voto recebido.
      // Ausente em enquete sem voto e em toda enquete anterior à feature de votos.
      const contagem = parseTalvezJson<number[]>(c.votosContagem);
      // Anota TODA opção quando há contagem, zero incluído: anotar só as votadas
      // faria a opção com 0 voto parecer uma enquete sem dado nenhum.
      const contagemValida = Array.isArray(contagem) && contagem.length > 0 ? contagem : undefined;
      const votosDe = (i: number) =>
        contagemValida ? ` — ${contagemValida[i] ?? 0} voto(s)` : "";
      const lista = Array.isArray(opcoes)
        ? opcoes
            .map((o, i) => `<li>${esc(typeof o === "string" ? o : o.opcao)}${votosDe(i)}</li>`)
            .join("")
        : "";
      return `<div class="texto">📊 Enquete: <b>${esc(c.nome)}</b></div><div class="opcoes"><ul>${lista}</ul></div>`;
    }

    case "PIX":
      return `<div class="anexo">💸 Pix — Titular: <b>${esc(c.titular)}</b> · Chave (${esc(c.tipo_chave)}): <b>${esc(c.chave)}</b></div>`;

    case "INTERATIVA": {
      const botoes = parseTalvezJson<BotaoInterativo[]>(c.botoes);
      const lista = Array.isArray(botoes)
        ? botoes
            .map(
              (b) =>
                `<span class="botao">${esc(b.texto || b.titulo || b.id)}</span>`,
            )
            .join(" ")
        : "";
      return `<div class="texto">${esc(c.corpo)}</div>${c.rodape ? `<div class="legenda">${esc(c.rodape)}</div>` : ""}${lista ? `<div class="opcoes">${lista}</div>` : ""}`;
    }

    case "CONVITE_GRUPO":
      return `<div class="anexo">👥 Convite para o grupo <b>${esc(c.nomeGrupo)}</b>${c.caption ? ` — ${esc(c.caption)}` : ""}</div>`;

    // Sem o caso explícito o `default` cairia em "[NAO_SUPORTADA]" — o nome do
    // enum cru no PDF que o cliente recebe.
    case "NAO_SUPORTADA":
      return `<div class="anexo">⚠️ Mensagem de um tipo que o WhatsApp não suporta${c.motivo ? ` — ${esc(String(c.motivo))}` : ""}</div>`;

    default: {
      const texto =
        c.texto || c.corpo || c.legenda || c.titulo || c.descricao || c.nome;
      return texto
        ? `<div class="texto">${esc(texto)}</div><div class="sistema">(${esc(tipo)})</div>`
        : `<div class="sistema">[${esc(tipo)}]</div>`;
    }
  }
}

type DadosResposta = {
  texto?: unknown;
  legenda?: unknown;
  tipo?: unknown;
  enviadaPorMim?: boolean | string;
  contato?: { nome?: unknown } | string | null;
};

function renderResposta(m: { dadosResposta?: DadosResposta | null }): string {
  const r = m.dadosResposta;
  if (!r) return "";
  let quem = "mensagem anterior";
  const contato =
    typeof r.contato === "string"
      ? parseTalvezJson<{ nome?: unknown }>(r.contato)
      : r.contato;
  if (contato && typeof contato === "object" && contato.nome)
    quem = toStr(contato.nome);
  else if (String(r.enviadaPorMim) === "true") quem = "atendente";
  const trecho = r.texto || r.legenda || `[${toStr(r.tipo) || "mídia"}]`;
  return `<div class="reply">↩ em resposta a <b>${esc(quem)}</b>: ${esc(toStr(trecho).slice(0, 140))}</div>`;
}

function gerarHtml(dados: ExportarConversaResponse, timeZone: string): string {
  const { contato, inbox, atendentes, mensagens } = dados;
  const atendentePorId = new Map(atendentes.map((a) => [a.id, a.nome]));

  let corpo = "";
  let diaAtual = "";
  for (const m of mensagens) {
    const data = dataDaMensagem(m);
    const dia = fmtDia(data, timeZone);
    if (dia !== diaAtual) {
      diaAtual = dia;
      corpo += `<div class="dia">${esc(dia)}</div>`;
    }

    const minha = m.enviadaPorMim === true;
    const autor = minha
      ? (m.idUsuario && atendentePorId.get(m.idUsuario)) ||
        (m.enviadaPelaApi ? "Sistema/API" : "Atendente")
      : contato.nome;

    corpo += `
      <div class="msg ${minha ? "out" : "in"}">
        <div class="meta"><b>${esc(autor)}</b> — ${esc(fmtData(data, timeZone))}${m.editada ? " (editada)" : ""}</div>
        ${renderResposta(m)}
        ${renderConteudo(m)}
      </div>`;
  }

  return `<!DOCTYPE html>
<html lang="pt-BR"><head><meta charset="utf-8">
<title>Conversa — ${esc(contato.nome)} (${esc(contato.codigo)})</title>
<style>
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { font-family: "Segoe UI", Arial, sans-serif; background: #ece5dd; margin: 0; padding: 24px; }
  header { background: #075e54; color: #fff; padding: 16px 24px; border-radius: 8px; margin-bottom: 16px; }
  header h1 { margin: 0 0 4px; font-size: 20px; }
  header p { margin: 0; font-size: 13px; opacity: .9; }
  .dia { font-size: 12px; color: #555; background: #d9d0c7; display: table; padding: 4px 12px; border-radius: 12px; margin: 18px auto 8px; text-align: center; }
  .msg { max-width: 70%; padding: 8px 12px; margin: 6px 0; border-radius: 8px; box-shadow: 0 1px 1px rgba(0,0,0,.15); page-break-inside: avoid; }
  .msg.in { background: #fff; margin-right: auto; }
  .msg.out { background: #dcf8c6; margin-left: auto; }
  .meta { font-size: 11px; color: #667; margin-bottom: 4px; }
  .texto { font-size: 14px; white-space: pre-wrap; word-break: break-word; }
  .legenda { font-size: 13px; color: #333; margin-top: 4px; }
  .anexo { font-size: 13px; background: #f0f0f0; border-left: 3px solid #075e54; padding: 6px 10px; border-radius: 4px; }
  .anexo ul, .opcoes ul { margin: 4px 0 0 16px; padding: 0; }
  .opcoes { font-size: 13px; color: #333; margin-top: 4px; }
  .botao { display: inline-block; border: 1px solid #075e54; color: #075e54; border-radius: 12px; padding: 2px 10px; font-size: 12px; margin-top: 4px; }
  .reply { font-size: 12px; color: #555; border-left: 3px solid #34b7f1; padding: 4px 8px; margin-bottom: 4px; background: rgba(52,183,241,.08); border-radius: 4px; }
  .sistema { font-size: 12px; font-style: italic; color: #777; }
  a { color: #0b66c3; }
  @page { margin: 12mm 8mm; }
  @media print { body { background: #fff; padding: 0; } .msg { box-shadow: none; } .msg.in { border: 1px solid #e0e0e0; } }
</style></head><body>
<header>
  <h1>${esc(contato.nome)}</h1>
  <p>Telefone: ${esc(contato.codigo)} · Inbox: ${esc(inbox.nome)} · ${mensagens.length} mensagens · Exportado em ${esc(fmtData(new Date(dados.geradoEm), timeZone))}</p>
  <p>Horários no fuso: ${esc(timeZone)} · Fotos, áudios, vídeos e documentos aparecem como links clicáveis ("abrir mídia").</p>
</header>
${corpo}
</body></html>`;
}

/** Fuso horário do navegador (ex.: "America/Sao_Paulo"); usado como padrão. */
export function fusoHorarioDoNavegador(): string {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Sao_Paulo"
    );
  } catch {
    return "America/Sao_Paulo";
  }
}

export function exportarConversaParaPdf(
  dados: ExportarConversaResponse,
  timeZone: string = fusoHorarioDoNavegador(),
): void {
  const html = gerarHtml(dados, timeZone);
  const win = window.open("", "_blank");
  if (!win) {
    throw new Error(
      "Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.",
    );
  }
  win.document.write(html);
  win.document.close();
  win.focus();
  // pequeno atraso garante a renderização do layout antes do diálogo de impressão
  setTimeout(() => win.print(), 300);
}
